import waitUntil from 'async-wait-until';
import {
  removeClass,
  addClass,
  closest,
  createEvent,
  monitorInputValueChanges,
} from '@utils/domUtils';

/* global jQuery */

export default class JobProfileSelector {
  static selectorString() {
    return '.JobProfileSelector';
  }

  getIndustryName() {
    const selectedIndustryId = this.getSelectedIndustryId();
    const selectedIndustry = this.industry.querySelector(`option[value="${selectedIndustryId}"]`);
    return selectedIndustry ? selectedIndustry.innerHTML : null;
  }

  getSubIndustryName() {
    const selectedSubIndustryId = this.getSelectedSubIndustryId();
    const selectedSubIndustry = this.subIndustry.querySelector(
      `option[value="${selectedSubIndustryId}"]`,
    );
    return selectedSubIndustry ? selectedSubIndustry.innerHTML : null;
  }

  getSelectedJobProfile() {
    return this.jobProfiles.find(element => element.input.checked);
  }

  getJobProfileName() {
    const selectedJobProfile = this.getSelectedJobProfile();

    if (selectedJobProfile) {
      return selectedJobProfile.jobProfileTitle;
    }

    if (this.setCustomJobProfileInput.checked && this.customJobProfileInput.value.length > 0) {
      return this.customJobProfileInput.value;
    }

    return null;
  }

  getJobProfileJobTitle() {
    const selectedJobProfile = this.getSelectedJobProfile();

    if (selectedJobProfile && selectedJobProfile.defaultJobTitle) {
      return selectedJobProfile.defaultJobTitle;
    }

    return this.getJobProfileName();
  }

  async render(node, props) {
    this.hasJob = !!props.job;
    this.firstTimeSelection = true;

    this.jobCreateWizard = closest(node, '.JobCreateWizard');
    this.jobAdEditor = this.jobCreateWizard.querySelector('.JobAdEditor');
    this.jobAdImageSelector = this.jobCreateWizard.querySelector('.JobAdImageSelector');
    this.jobCreateGeneral = this.jobCreateWizard.querySelector('.JobCreateGeneral');

    await waitUntil(() => this.jobAdEditor.component);
    await waitUntil(() => this.jobAdImageSelector.component);
    await waitUntil(() => this.jobCreateGeneral.component);

    this.industry = node.querySelector('[name="industry"]');
    this.subIndustry = node.querySelector('[name="subIndustry"]');

    this.subIndustryOptions = [...this.subIndustry.querySelectorAll('.select-option')].reduce(
      (result, subIndustryOption) => {
        const subIndustryId = parseInt(subIndustryOption.getAttribute('value'));

        return {
          ...result,
          [subIndustryId]: {
            id: subIndustryId,
            text: jQuery.trim(subIndustryOption.innerHTML),
            industryId: parseInt(subIndustryOption.getAttribute('data-industry')),
            node: subIndustryOption,
          },
        };
      },
      {},
    );

    this.industryOptions = [...this.industry.querySelectorAll('.select-option')].reduce(
      (result, industryOption) => {
        const industryId = parseInt(industryOption.getAttribute('value'));
        const subIndustryIds = JSON.parse(industryOption.getAttribute('data-sub-industries'));

        return {
          ...result,
          [industryId]: {
            subIndustryIds,
            subIndustries: subIndustryIds.map(id => this.subIndustryOptions[id]),
          },
        };
      },
      {},
    );

    this.setCustomJobProfileInput = node.querySelector(
      '[name="jobProfile"][value="setCustomJobProfile"]',
    );
    this.customJobProfileInput = node.querySelector('[name="customJobProfile"]');

    this.jobProfileList = node.querySelector('.job-profile-list');
    this.jobProfiles = [...node.querySelectorAll('.job-profile-option')];
    this.jobProfiles.forEach(element => {
      element.industryIds = JSON.parse(element.getAttribute('data-industries'));
      element.subIndustryIds = JSON.parse(element.getAttribute('data-sub-industries'));
      element.jobProfileId = parseInt(element.getAttribute('data-job-profile-id'));
      element.jobProfileTitle = jQuery.trim(element.getAttribute('data-title'));
      element.defaultJobTitle = jQuery.trim(element.getAttribute('data-default-job-title'));
      element.defaultJobImages = JSON.parse(
        jQuery.trim(element.getAttribute('data-default-job-images')),
      );
      element.input = element.querySelector('[name="jobProfile"]');
      element.sections = [...element.querySelectorAll('.job-profile-sections [data-section]')].map(
        getSectionData,
      );
    });

    this.customJobProfileSections = [
      ...node.querySelectorAll('.custom-job-profile-sections [data-section]'),
    ].map(getSectionData);

    this.selectJobProfileWrapper = node.querySelector('.select-job-profile-wrapper');
    this.selectSubIndustryWrapper = node.querySelector('.select-sub-industry-wrapper');

    const industryPlaceholder =
      this.industry.getAttribute('placeholder') ||
      this.industry.querySelector('option[value=""]').innerHTML;

    const subIndustryPlaceholder =
      this.subIndustry.getAttribute('placeholder') ||
      this.subIndustry.querySelector('option[value=""]').innerHTML;

    jQuery(this.industry)
      .select2({
        placeholder: jQuery.trim(industryPlaceholder),
        allowClear: true,
        theme: 'default large_style',
      })
      .on('select2:select', this.selectIndustry.bind(this))
      .on('select2:unselect', this.selectIndustry.bind(this));

    jQuery(this.subIndustry)
      .select2({
        placeholder: jQuery.trim(subIndustryPlaceholder),
        allowClear: true,
        theme: 'default large_style',
        data: [],
      })
      .on('select2:select', this.selectSubIndustry.bind(this))
      .on('select2:unselect', this.selectSubIndustry.bind(this));

    this.industry.addEventListener('change', this.selectIndustry.bind(this));
    this.subIndustry.addEventListener('change', this.selectSubIndustry.bind(this));

    this.setCustomJobProfileInput.addEventListener('change', this.selectJobProfile.bind(this));
    this.jobProfiles.forEach(element => {
      element.input.addEventListener('change', this.selectJobProfile.bind(this));
    });

    // NOTE: also update job profile stuff when changing text in custom job profile input
    // i.e. update jobTitle in step `general` accordingly
    monitorInputValueChanges(this.customJobProfileInput);
    this.customJobProfileInput.addEventListener(
      'value:changed',
      this.changedCustomJobProfile.bind(this),
    );

    this.filter();
  }

  getSelectedIndustryId() {
    return isNaN(this.industry.value) || String(this.industry.value) === ''
      ? null
      : parseInt(this.industry.value);
  }

  getSelectedSubIndustryId() {
    return isNaN(this.subIndustry.value) || String(this.subIndustry.value) === ''
      ? null
      : parseInt(this.subIndustry.value);
  }

  selectIndustry() {
    this.industry.dispatchEvent(createEvent('validate'));
    this.filter();
  }

  selectSubIndustry() {
    this.subIndustry.dispatchEvent(createEvent('validate'));
    this.filter();
  }

  filter() {
    const selectedIndustryId = this.getSelectedIndustryId();
    const currentSelectedSubIndustryId = this.getSelectedSubIndustryId();

    Object.keys(this.subIndustryOptions).forEach(id => {
      const subIndustry = this.subIndustryOptions[id];
      const node = subIndustry.node;

      if (subIndustry.industryId === selectedIndustryId) {
        if (!node.parentNode) {
          this.subIndustry.appendChild(node);
        }
      } else {
        // NOTE: deselect selected sub industry option if not in available anymore
        if (currentSelectedSubIndustryId === subIndustry.id) {
          this.subIndustry.value = null;
          this.subIndustry.dispatchEvent(createEvent('validate'));
        }

        if (node.parentNode) {
          node.parentNode.removeChild(node);
        }
      }
    });

    if ([...this.subIndustry.querySelectorAll('.select-option')].length === 1) {
      const currentSelectedSubIndustryId = this.getSelectedSubIndustryId();
      const selectSubIndustryId = this.subIndustry
        .querySelector('.select-option')
        .getAttribute('value');

      if (String(currentSelectedSubIndustryId) !== String(selectSubIndustryId)) {
        this.subIndustry.value = selectSubIndustryId;
        this.subIndustry.dispatchEvent(createEvent('validate'));
      }
    }

    // NOTE: force select2 to refresh and reload options
    jQuery(this.subIndustry).trigger('change');

    // NOTE: only retrieve selected sub industry id after sub industry select has been refreshed above
    const selectedSubIndustryId = this.getSelectedSubIndustryId();

    this.jobProfiles.forEach(element => {
      const hasIndustry = element.industryIds.indexOf(selectedIndustryId) !== -1;
      const hasSubIndustry = element.subIndustryIds.indexOf(selectedSubIndustryId) !== -1;
      const isVisible =
        (hasIndustry || selectedIndustryId === null) &&
        (hasSubIndustry || selectedSubIndustryId === null);

      if (isVisible) {
        removeClass(element, 'hide');
      } else {
        addClass(element, 'hide');

        if (element.input.checked) {
          element.input.checked = false;
          element.input.dispatchEvent(createEvent('validate'));
        }
      }
    });

    if (selectedIndustryId === null) {
      addClass(this.selectSubIndustryWrapper, 'hide');
    } else {
      removeClass(this.selectSubIndustryWrapper, 'hide');
    }

    if (selectedIndustryId === null || selectedSubIndustryId === null) {
      addClass(this.selectJobProfileWrapper, 'hide');
      this.setCustomJobProfileInput.checked = false;
      this.customJobProfileInput.value = '';
      this.customJobProfileInput.dispatchEvent(createEvent('validate'));

      // uncheck all checkboxes
      this.jobProfiles.forEach(element => {
        if (element.input.checked) {
          element.input.checked = false;
          element.input.dispatchEvent(createEvent('validate'));
        }
      });

      if (this.setCustomJobProfileInput.checked) {
        this.setCustomJobProfileInput.checked = false;
        this.setCustomJobProfileInput.dispatchEvent(createEvent('validate'));
      }
    } else {
      removeClass(this.selectJobProfileWrapper, 'hide');
    }

    // NOTE: override values if job profile got deselected while filtering
    this.selectJobProfile();
  }

  selectJobProfile() {
    // TODO: implement popup that asks whether you want to override values after ad content has already been changed...
    const selectedJobProfile = this.getSelectedJobProfile();
    const selectedJobProfileSections = selectedJobProfile
      ? selectedJobProfile.sections
      : this.setCustomJobProfileInput.checked
      ? this.customJobProfileSections
      : null;

    const sections = selectedJobProfileSections
      ? selectedJobProfileSections.reduce(
          (result, sectionData) => ({
            ...result,
            [sectionData.id]: sectionData,
          }),
          {},
        )
      : {};

    if (!this.hasJob || !this.firstTimeSelection) {
      const defaultJobTitle = this.getJobProfileJobTitle();
      this.jobAdEditor.component.setSectionData(sections);
      this.jobCreateGeneral.component.setJobTitle(defaultJobTitle);
    }

    const jobAdImages = selectedJobProfile ? selectedJobProfile.defaultJobImages : [];
    this.jobAdImageSelector.component.setJobAdImages(jobAdImages);

    // NOTE: reset custom job profile input text if set custom job profile is not checked
    // i.e. keep UI cleaner and less confusing
    if (!this.setCustomJobProfileInput.checked) {
      this.customJobProfileInput.value = '';
      this.customJobProfileInput.dispatchEvent(createEvent('validate'));
    }

    this.firstTimeSelection = false;
  }

  changedCustomJobProfile() {
    // NOTE: select custom job profile radio button if entering data in custom job profile input
    if (!this.setCustomJobProfileInput.checked) {
      this.setCustomJobProfileInput.checked = true;
      this.setCustomJobProfileInput.dispatchEvent(createEvent('validate'));
    }

    this.selectJobProfile();
  }
}

function getSectionData(section) {
  return {
    id: section.getAttribute('data-section'),
    title: section.querySelector('[data-prop="title"]').innerHTML,
    description: section.querySelector('[data-prop="description"]').innerHTML,
  };
}
